import { useEffect, useState } from 'react';

import { PersonalizationWidgetProvider } from '@vp/design-personalization-widget';
import { FetchClientExecutor } from '@vp/fetch-client';
import { CustomLoggerProvider, useCustomLogger } from '@vp/pdp-ubik-logging';
import { Box, ScreenClassProvider } from '@vp/swan';
import { IdentityProvider, useSwanStyleKeys } from '@vp/ubik-context';
import { LocalizationsProvider, NestedStringObject } from '@vp/ubik-localizations-provider';

import { AuthProvider } from '../contexts/AuthProvider';

import { PersonalizationCardWrapper } from './brandkit';
import {
  FAVORITES_HEART_SWAN_STYLE_KEYS,
  FavoritesHeartWrapper,
  FavoritesHeartWrapperProps,
} from './favorites-heart';
import {
  HERO_GALLERY_SWAN_STYLES,
  PDPHeroCarousel,
  PDPHeroCarouselProps,
} from './pdp-hero-carousel';

export interface FragmentProps {
  pdpHeroCarouselProps: PDPHeroCarouselProps;
  favoriteHeartProps?: FavoritesHeartWrapperProps;
  locale: string;
  mpvId: string;
  localizations: NestedStringObject;
  personalizationWidgetLocalizations: NestedStringObject;
  environment?: string;
  designPersonalizationContextIdOverride?: string;
}

export const Fragment: React.FC<FragmentProps> = (props) => {
  useSwanStyleKeys([...HERO_GALLERY_SWAN_STYLES, ...FAVORITES_HEART_SWAN_STYLE_KEYS]); // TODO: check if duplicates are ok

  return (
    <IdentityProvider auth={{ culture: props.locale, developmentMode: false }}>
      <AuthProvider>
        <ScreenClassProvider>
          <CustomLoggerProvider
            context={{ mpvId: props.mpvId, locale: props.locale, environment: props.environment }}
          >
            <WrappedFragment {...props} />
          </CustomLoggerProvider>
        </ScreenClassProvider>
      </AuthProvider>
    </IdentityProvider>
  );
};

const WrappedFragment: React.FC<FragmentProps> = (props) => {
  const { logger } = useCustomLogger();
  const [hasBrandkitApiError, setHasBrandkitApiError] = useState(false);
  const hasDesignPersonalizationContextIdOverride = !!props.designPersonalizationContextIdOverride;
  const shouldShowBrandKit =
    props.pdpHeroCarouselProps.showBrandKit || hasDesignPersonalizationContextIdOverride;

  useEffect(() => {
    FetchClientExecutor.initializeWithLogger(logger);
  }, [logger]);

  return (
    <LocalizationsProvider logger={logger} localizations={props.localizations}>
      <Box>
        <Box
          marginX={{
            xs: '0',
            sm: '8',
            md: '8',
            lg: '8',
            xl: '8',
          }}
          padding={'4'}
          style={{ position: 'absolute', top: 0, right: 0, zIndex: 3 }}
        >
          {props.favoriteHeartProps && <FavoritesHeartWrapper {...props.favoriteHeartProps} />}
        </Box>
        {shouldShowBrandKit ? (
          <PersonalizationWidgetProvider
            localizations={props.personalizationWidgetLocalizations}
            localStoragePageKey="pdp"
            configuration={{
              allowRestoreFromBrand: true,
              personalizationToggleOffByDefault: true,
            }}
          >
            <PDPHeroCarousel
              {...props.pdpHeroCarouselProps}
              onBrandkitApiError={setHasBrandkitApiError}
              designPersonalizationContextIdOverride={props.designPersonalizationContextIdOverride}
              showBrandKit={shouldShowBrandKit}
            />

            <Box
              mt="to-actions"
              paddingBottom={{
                xs: 6,
                sm: 6,
                md: 0,
                lg: 0,
              }}
            >
              <PersonalizationCardWrapper hasApiError={hasBrandkitApiError} />
            </Box>
          </PersonalizationWidgetProvider>
        ) : (
          <PDPHeroCarousel {...props.pdpHeroCarouselProps} />
        )}
      </Box>
    </LocalizationsProvider>
  );
};
